import type { BuildFinancialAnalysisInput, BuildFinancialAnalysisResult } from './buildFinancialAnalysis';
import { computeDataCenterFinance } from './dataCenterFinance';
import { scoreSiteFinancial, type FinancialScoreResult } from './scoreSiteFinancial';
import { dataCenterFinancialModelV1Schema } from '../types/dataCenterFinancial';
import type { FinancialAnalysisStamp } from '../types/siteRag';

/** Deterministic snapshot; model outputs and source comparison figures stay separate. */
export function buildDataCenterFinancialAnalysis(input: BuildFinancialAnalysisInput): BuildFinancialAnalysisResult {
  const parsed = dataCenterFinancialModelV1Schema.safeParse(input.financialAssumptions?.dataCenterModel);
  const result = computeDataCenterFinance(input.financialAssumptions?.dataCenterModel);
  const model = parsed.success ? parsed.data : null;
  const limitations = model ? [
    ...(model.authority === 'fictional-scenario' ? ['Fictional scenario; no real investment recommendation or commitment.'] : []),
    ...model.unpricedScopes.map(scope => `Unpriced scope: ${scope}.`),
    ...model.decisionLimitations,
  ] : [];
  const hypothetical = model?.authority === 'fictional-scenario';
  const context = input.demonstrationFinancialContext;
  const canonicalHypothetical = Boolean(hypothetical && context
    && context.siteId === model?.siteId
    && context.ownerUid === model?.ownerUid
    && context.scenarioId === model?.scenarioId);
  const scopeReason = hypothetical && !canonicalHypothetical
    ? ['Canonical demonstration owner, site and scenario context is missing or does not match the saved financial model.']
    : [];
  // A fully priced canonical fictional model may be graded as a hypothetical
  // calculation. Neither its band nor its source inputs gain real-world
  // investment authority. Partial budgets and unmatched scope stay withheld.
  const scopeLimited = (hypothetical && !canonicalHypothetical) || Boolean(model?.unpricedScopes.length);
  const scopeLimitations = [...limitations, ...scopeReason];
  const financialScore = scoreSiteFinancial({ npvUsd: result.npvUsd, projectIrrPct: result.irrPct,
    hurdleRatePct: model?.discountRatePct, paybackYears: result.paybackYears,
    projectLifeYears: model ? Math.max(0, model.periods.length - 1) : null, pPositiveNpv: null });
  const score: FinancialScoreResult = !result.computable || scopeLimited
    ? { score: null, band: null, rationale: !result.computable ? result.issues : scopeLimitations,
      bindingConstraints: !result.computable ? result.issues : scopeLimitations }
    : financialScore;
  const stamp: FinancialAnalysisStamp = {
    modelKind: 'data-center-capacity-fee',
    ...(canonicalHypothetical && result.computable ? { decisionScope: {
      kind: 'hypothetical-scenario' as const, scenarioId: model!.scenarioId!,
      calculated: true as const, realWorldEligible: false as const,
    } } : {}),
    ...(model ? { dataCenter: { model, result, lcoeApplicability: 'not-applicable' as const } } : {}),
    headline: { lcoeUsdPerMwh: null, lcoeRealUsdPerMwh: null, npvUsd: result.npvUsd,
      irrPct: result.irrPct, hurdleRatePct: model?.discountRatePct ?? null, paybackYears: result.paybackYears,
      projectLifeYears: model ? Math.max(0, model.periods.length - 1) : null,
      capexPerKwUsd: null, annualMwh: null, waccPct: null },
    monteCarlo: { npvP10: null, npvP50: null, npvP90: null, pPositiveNpv: null, basis: null },
    pathwayStatus: 'none',
    ...(result.computable ? { cashflow: { basis: 'data-center-annual' as const,
      baseYear: model!.referenceYear, flowsUsd: result.ledger.map(row => row.netCashUsd),
      cumulativeUsd: result.ledger.map(row => row.cumulativeCashUsd) } } : {}),
    rationale: [...score.rationale, ...(canonicalHypothetical ? limitations : []), 'Capacity-fee revenue includes electricity; grid energy and facility demand charges are separate costs. LCOE is not applicable.'],
    bindingConstraints: score.bindingConstraints, band: score.band, viabilityScore: score.score,
    ...(score.band == null ? { verdictWithheldReason: !result.computable ? 'inputs-insufficient' as const
      : scopeLimited ? 'scope-limited' as const : 'inputs-incoherent' as const } : {}),
    computedAt: (input.now ?? new Date()).toISOString(),
    actionPlan: { recommendation: score.band == null
      ? 'Financial verdict withheld. The annual data-center capacity-fee ledger is a screening calculation, subject to its stated input and scope limits.'
      : canonicalHypothetical
        ? `Hypothetical scenario ${model!.scenarioId}: calculated financial screening only; not eligible for a real-world investment decision or commitment.`
        : 'Data-center capacity-fee financial screening; validate the quoted assumptions and stated limits before a commitment.',
    actions: (!result.computable ? result.issues : scopeLimitations).map(issue =>
      `Resolve financial basis: ${issue} owner: Finance lead; deadline: before financial commitment.`) },
  };
  return { stamp, overallScore: score.score, score };
}
